"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ANY_AUTHENTICATED = exports.permissionSchema = exports.PERMISSIONS = exports.PERMISSION_PATTERN = exports.roleBaseTypeSchema = exports.ROLE_BASE_TYPES = void 0;
exports.isPermission = isPermission;
const zod_1 = require("zod");
/**
 * Rechte und Grundrollen-Typen (AC-007).
 *
 * Der Schnitt kommt aus dem MVP-Bericht, Kapitel 6.1: Rollennamen und
 * Hierarchie-Tiefe sind SBI-KONFIGURATION, die Grundrollen-Typen sind
 * ASAS-CORE-STANDARD. Daraus folgt, was hier steht und was nicht:
 *
 *   - RECHTE stehen hier, im Code, als geschlossener Satz. Der Anwendungscode
 *     prueft immer gegen ein Recht und NIE gegen einen Rollennamen.
 *   - ROLLEN stehen nicht hier, sondern als Zeilen in der Datenbank. Die acht
 *     SBI-Rollen sind Daten; ein zweiter Mandant bringt eigene Namen mit, ohne
 *     dass jemand TypeScript anfasst.
 *
 * Die acht SBI-Rollen und ihre Rechtebuendel stehen deshalb ausschliesslich in
 * der Migration. Eine Kopie an dieser Stelle waere eine zweite Wahrheit — und
 * zwar die, die beim naechsten Mandanten falsch ist.
 *
 * Diese Datei ist transportfrei (ADR-04): nur Zod, kein tRPC, kein Prisma,
 * kein NestJS.
 */
/**
 * Die vier Grundrollen-Typen. Produkt, nicht Konfiguration.
 *
 * Jede mandantenspezifische Rolle ordnet sich genau einem Typ zu. Der Typ
 * traegt selbst KEINE Rechte — er ist die Klammer, an der spaetere Storys
 * ansetzen koennen (AC-008 Rollenumschalter, AC-806 Sichtbarkeit), ohne acht
 * SBI-Namen kennen zu muessen. Wer hier Rechte anhaengt, hat die Trennung aus
 * Kapitel 6.1 wieder eingerissen.
 */
exports.ROLE_BASE_TYPES = [
    'management',
    'projektsteuerung',
    'mitarbeiter',
    'administration',
];
exports.roleBaseTypeSchema = zod_1.z.enum(exports.ROLE_BASE_TYPES);
/**
 * Die Form eines Rechte-Bezeichners: `bereich:verb`, beides klein.
 *
 * Exportiert und nicht im Test versteckt, weil der Katalogabgleich gegen die
 * Datenbank (AC-007, Etappe 2) dieselbe Form braucht. Zwei Regex an zwei Orten
 * laufen auseinander, und die eine davon waere irgendwann die laxere.
 *
 * Der Doppelpunkt ist dabei nicht Kosmetik: er trennt den Katalog sauber von
 * ANY_AUTHENTICATED weiter unten, das bewusst KEINEN traegt und deshalb nie
 * versehentlich als Recht durchgehen kann.
 */
exports.PERMISSION_PATTERN = /^[a-z][a-z0-9_]*:[a-z][a-z0-9_]*$/;
/**
 * Der geschlossene Rechtekatalog.
 *
 * ABSICHTLICH KURZ. Ein Recht kommt dazu, wenn eine Prozedur es tatsaechlich
 * prueft — nicht, weil es plausibel klingt. Ein Recht ohne pruefende Prozedur
 * steht im Seed wie eine Zusage und ist keine: es weckt den Eindruck, eine
 * Funktion sei abgesichert, die es gar nicht gibt.
 *
 * Wer die Rollen- und Nutzerverwaltung bekommt, ist mit AC-107 entschieden:
 * die zwei Rechte `principals:read` und `principals:manage` weiter unten.
 */
exports.PERMISSIONS = [
    /**
     * Eine Ressourcenanfrage freigeben, verschieben oder ablehnen (AK-2).
     *
     * Der Projektleiter hat dieses Recht NICHT — darin sind sich MVP-Bericht,
     * Lastenheft 2020 und die Workflow-Validierung einig. WER es stattdessen
     * hat, ist zwischen den Quellen strittig (Leiter Projekte gegen
     * Fachgruppenleitung) und steht deshalb als Datenzeile in der Migration,
     * nicht hier: die Korrektur ist dann ein DELETE und keine Codeaenderung.
     */
    'resource_allocation:approve',
    /**
     * Die Aenderungshistorie eines Datensatzes lesen (AC-009, AK-3).
     *
     * Deckt `time_entries`, `work_packages` und die PACS-Spiegel ab — nicht
     * `projects`, siehe das naechste Recht.
     */
    'audit:read',
    /**
     * Die Historie honorarfuehrender Datensaetze lesen.
     *
     * Eigenes Recht, weil in `projects` das HONORAR steht und die Historie es
     * mit Alt- und Neuwert ausliefert. Genau das ist die offene Luecke, die
     * CLAUDE.md Paragraph 16 dieser Story zuweist: bis heute sieht jede
     * angemeldete Person jede Honoraraenderung samt Urheber. Ein einziges
     * `audit:read` wuerde die Luecke nur verschieben, nicht schliessen.
     */
    'audit:read_fee',
    /** Den Zustand des naechtlichen PACS-Laufs lesen (AC-504, AK-5). */
    'pacs:read_status',
    /**
     * PACS-Projekte, ihre Schritte samt Zuordnungsstand und den Abdeckungsgrad
     * lesen (AC-505). Eigenes Recht und nicht Teil von `pacs:map_steps`: das
     * Management braucht die Zahl fuer die Jour-Fixe-Entscheidung, soll aber
     * nicht festlegen, auf welchem Arbeitspaket Ist-Stunden landen.
     */
    'pacs:read_mapping',
    /**
     * Einen PACS-Schritt einem Arbeitspaket zuordnen oder die Zuordnung aendern
     * (AC-505). Damit wandern Ist-Stunden — eine Aktion, keine Einsicht. Wer es
     * hat, steht als Datenzeile in der Migration `*_ac505_pacs_step_mappings`.
     */
    'pacs:map_steps',
    /**
     * Die Konfiguration des EIGENEN Mandanten aendern (AC-101): Rollennamen,
     * LPH-Gruppierung, Schwellwerte und aktive Module.
     *
     * Lesen braucht kein Recht — Rollennamen und Gruppierung zeigt jede
     * Oberflaeche an. Aendern schon, denn ein abgeschaltetes Modul trifft jede
     * Person im Mandanten auf einen Schlag. Einen Mandanten ANLEGEN kann damit
     * niemand: das ist ein Betriebshandgriff als Migrator
     * (`docs/runbooks/mandant-anlegen.md`), kein Recht innerhalb eines Mandanten.
     */
    'tenant:configure',
    /**
     * Konten des EIGENEN Mandanten lesen: Status (aktiv/gesperrt) und
     * zugewiesene Rollen (AC-107). Eigenes Recht statt `ANY_AUTHENTICATED`, weil
     * die Liste E-Mail-Adressen und Namen aller Kolleginnen und Kollegen
     * enthaelt — kein Datum, das jede angemeldete Person sehen soll.
     */
    'principals:read',
    /**
     * Ein Konto sperren, entsperren oder ihm eine Rolle zuweisen/entziehen
     * (AC-107, AK-1 und AK-2). Erzeugt keine neuen Rollen und aendert nicht,
     * welche Rechte eine Rolle traegt — das bleibt ausschliesslich der
     * Migration vorbehalten (Regel 20).
     */
    'principals:manage',
];
exports.permissionSchema = zod_1.z.enum(exports.PERMISSIONS);
/**
 * „Jede angemeldete Person" — ausdruecklich erklaert, nicht durch Weglassen.
 *
 * Der Unterschied ist der ganze Punkt des Fail-closed-Entwurfs: Eine Prozedur
 * OHNE Zugriffsangabe ist ein Versehen und wird zur Laufzeit abgelehnt. Eine
 * Prozedur, die bewusst jeder Angemeldete aufrufen darf, sagt das mit diesem
 * Wert. Ohne ihn gaebe es keinen Weg, „offen fuer alle" auszudruecken — und
 * der erste, der ihn braeuchte, wuerde die Pflichtangabe aufweichen.
 *
 * Kein Doppelpunkt, deshalb nach PERMISSION_PATTERN nie ein gueltiges Recht.
 */
exports.ANY_AUTHENTICATED = 'any_authenticated';
const PERMISSION_SET = new Set(exports.PERMISSIONS);
/**
 * Prueft einen Wert UNBEKANNTER Herkunft gegen den Katalog.
 *
 * Gebraucht wird das am Rand zur Datenbank: die Rechte eines Nutzers kommen
 * dort als Text heraus. Ohne diese Pruefung waere der Union-Typ `Permission`
 * eine Behauptung — ein Wert, den eine aeltere oder von Hand veraenderte Zeile
 * mitbringt, liefe als gueltiges Recht durch den gesamten Code.
 *
 * Bewusst KEIN Trimmen und keine Kleinschreibung: ' audit:read' ist kein
 * Recht, sondern ein Fehler in den Daten, und der soll auffallen.
 */
function isPermission(value) {
    return PERMISSION_SET.has(value);
}
//# sourceMappingURL=rbac.js.map